import { StreetOrientationPreference } from "building-types/building/streetOrientationTypesEnum";
import type { StreetOrientation } from "building-types/building/streetOrientationTypesEnum";
import type { FloorAssembly } from "building-types/floors/floorAssemblyTypes";
import type { OpeningPlacementOptions } from "building-types/openings/openingFuncOptions";
import type {
  RoomTypeOpeningPlacementTable,
  ManualOpeningPlacement,
  OpeningPlacement,
  RoomTypeOpeningPlacement,
} from "building-types/openings/openingPlacementTypes";
import { roomTypeOpeningPlacementNew } from "building-types/openings/openingPlacementTypes";
import { RoomFilter } from "building-types/openings/openingTypesEnum";
import type { Room } from "building-types/rooms/roomTypes";
import { RoomType } from "building-types/rooms/roomTypesEnum";
import { PathPriority } from "building-types/walls/pathPriority";
import type { Table } from "common/data/table";
import { voidFloorAssemblyNew } from "doc/elements/floor/floorAssembly";
import { isWalledRoomEnclosure } from "doc/rooms/roomAttributesTools";
import { defaultRoomCatalog } from "doc/rooms/roomCatalog";
import { floorPathBaseOffset } from "../floor/floorPathUtils";

export const allRoomTypes = Object.values(RoomType) as RoomType[];

const openConnectedRoomTypes = allRoomTypes.filter((t) => {
  return !!defaultRoomCatalog[t].openToAdjacent;
});

const notEnclosedRoomTypes = allRoomTypes.filter((t) => {
  return !isWalledRoomEnclosure[defaultRoomCatalog[t].roomEnclosure];
}) as RoomType[];

export function roomTypeOpeningPlacementTableNew(options?: OpeningPlacementOptions[]): RoomTypeOpeningPlacementTable {
  const innerTable: RoomTypeOpeningPlacementTable = {};

  for (const option of options || []) {
    const { roomStartTypes, roomStartFilter, roomEndTypes = [""] as string[], roomEndFilter } = option;

    // switch to string if API opened up to non-built-in roomtypes
    const roomStarts: RoomType[] = [];
    if (roomStartTypes?.length) {
      roomStarts.push(...roomStartTypes);
    }

    if (roomStartFilter) {
      roomTypeFilter(roomStartFilter, roomStarts);
    }

    const roomEnds: (RoomType | string)[] = [];
    if (roomEndTypes?.length) {
      roomEnds.push(...roomEndTypes);
    }

    if (roomEndFilter) {
      roomTypeFilter(roomEndFilter, roomEnds);
    }

    for (const roomStartType of roomStarts) {
      for (const roomEndType of roomEnds) {
        if (!innerTable[roomStartType]) {
          innerTable[roomStartType] = {};
        }

        if (!innerTable[roomStartType][roomEndType]) {
          innerTable[roomStartType][roomEndType] = {};
        }

        if (!option.streetOrientations || option.streetOrientations.length === 0) {
          innerTable[roomStartType][roomEndType][0] = roomTypeOpeningPlacementNew(option); // O = StreetOrientationPreference.UNDEFINED
        } else {
          for (const streetOrientation of option.streetOrientations) {
            innerTable[roomStartType][roomEndType][streetOrientation] = roomTypeOpeningPlacementNew(option);
          }
        }
      }
    }
  }

  return innerTable;
}

function roomTypeFilter(roomStartFilter: RoomFilter, roomTypes: RoomType[] | string[]) {
  switch (roomStartFilter) {
    case RoomFilter.ALL: {
      roomTypes.push(...allRoomTypes);
      break;
    }
    case RoomFilter.OPEN_CONNECTED: {
      roomTypes.push(...openConnectedRoomTypes);
      break;
    }
    case RoomFilter.NOT_ENCLOSED: {
      roomTypes.push(...notEnclosedRoomTypes);
      break;
    }
  }
}

export function openingPlacementLookup(
  builtInOpeningPlacementTable: RoomTypeOpeningPlacementTable,
  manualOpeningPlacements: ManualOpeningPlacement[] | undefined,
  start: Room | undefined,
  end: Room | undefined,
  startEdgeIndex: number,
  endEdgeIndex: number,
  streetOrientation: StreetOrientation,
): OpeningPlacement | undefined {
  const startType = start?.type ?? ("" as RoomType);
  const endType = end?.type ?? ("" as RoomType);

  const edgeIdPlacement = lookupManualOpeningPlacement(
    manualOpeningPlacements,
    start,
    end,
    startEdgeIndex,
    endEdgeIndex,
  );

  if (edgeIdPlacement) {
    return edgeIdPlacement;
  }

  const roomTypePlacement = lookupRoomTypeOpeningPlacement(
    builtInOpeningPlacementTable,
    startType,
    endType,
    streetOrientation,
  );

  if (!roomTypePlacement) {
    return undefined;
  }

  return { ...roomTypePlacement, precedence: roomTypePlacement.precedence || -1 };
}

function lookupManualOpeningPlacement(
  manualOpeningPlacements: ManualOpeningPlacement[] | undefined,
  startRoom: Room | undefined,
  endRoom: Room | undefined,
  startRoomEdgeIndex: number,
  endRoomEdgeIndex: number,
): ManualOpeningPlacement | undefined {
  if (!manualOpeningPlacements?.length) {
    return undefined;
  }
  if (!startRoom) {
    return undefined;
  }

  const edgeIds = startRoom.edgeIds;
  const edgeId = edgeIds[startRoomEdgeIndex];

  const locationId = startRoom.id;
  const blockId = startRoom.parentId;

  const endBlockId = endRoom?.parentId;
  const endLocationId = endRoom?.id;
  const endEdgeId = endRoom?.edgeIds[endRoomEdgeIndex];

  for (const placement of manualOpeningPlacements) {
    if (placement.blockId === blockId && placement.locationId === locationId && placement.edgeId === edgeId) {
      if (placement.exclusive === true) {
        return placement;
      }

      if (
        (!placement.oppositeBlockId || placement.oppositeBlockId === endBlockId) &&
        (!placement.oppositeLocationId || placement.oppositeLocationId === endLocationId) &&
        (!placement.oppositeEdgeId || placement.oppositeEdgeId === endEdgeId)
      ) {
        return placement;
      }
    }
  }

  return undefined;
}

function lookupRoomTypeOpeningPlacement(
  roomTypePlacements: RoomTypeOpeningPlacementTable,
  startType: RoomType,
  endType: RoomType = "" as RoomType,
  streetOrientation?: StreetOrientation,
): RoomTypeOpeningPlacement | undefined {
  const placements = roomTypePlacements[startType];
  if (!placements) {
    return undefined;
  }

  const placementByStreetOrientation = placements[endType];
  if (!placementByStreetOrientation) {
    return undefined;
  }

  if (streetOrientation) {
    const allOrientationsPlacement = placementByStreetOrientation[StreetOrientationPreference.ALL];
    if (allOrientationsPlacement) {
      return allOrientationsPlacement;
    }

    const orientationSpecificPlacement = placementByStreetOrientation[streetOrientation];
    if (orientationSpecificPlacement) {
      return orientationSpecificPlacement;
    }

    const placement = placementByStreetOrientation[StreetOrientationPreference.ANY];
    if (placement) {
      return placement;
    }
  }

  const placement = placementByStreetOrientation[StreetOrientationPreference.UNDEFINED];
  if (placement) {
    return placement;
  }

  return undefined;
}

export function computeOpeningZOffset(
  roomFloorAssemblies: Table<FloorAssembly>,
  frontRoom?: Room,
  backRoom?: Room,
): number {
  const frontRoomAssembly = frontRoom ? roomFloorAssemblies[frontRoom.id] : voidFloorAssemblyNew();
  const backRoomAssembly = backRoom ? roomFloorAssemblies[backRoom.id] : voidFloorAssemblyNew();

  return floorPathBaseOffset({
    leftFloorAssembly: frontRoomAssembly,
    rightFloorAssembly: backRoomAssembly,
    floorPriority: PathPriority.NONE,
  });
}
